// src/lib/subdomain.js
//
// Small shared helpers for organizer subdomain URLs. Used by:
//   - App.js: extracting the current subdomain's slug, and building the
//     redirect URL for the www/root -> own-subdomain auto-redirect
//   - OrganizerMismatch.js: building the "Go to my pool" redirect URL
//
// Kept in one place so the root-domain-reconstruction logic (which strips
// any existing subdomain and re-adds the target slug) can't silently
// drift between the two call sites.

// ROOT_DOMAIN is configurable via REACT_APP_ROOT_DOMAIN (a build-time env
// var -- baked in at build time, same as REACT_APP_API_URL, so changing
// it requires a rebuild+redeploy, not just a config edit) so the exact
// same subdomain-routing logic works correctly for both:
//   - production: heberthoops.com (default, if the env var is unset)
//   - staging:    staging.heberthoops.com
// Without this, a staging organizer subdomain like
// "heberts-pool.staging.heberthoops.com" would incorrectly extract
// "heberts-pool.staging" (dot and all) as the slug, since the old
// hardcoded ROOT_DOMAIN only ever stripped the fixed ".heberthoops.com"
// suffix regardless of how many labels came before it.
const ROOT_DOMAIN = process.env.REACT_APP_ROOT_DOMAIN || 'heberthoops.com';

// Extracts an organizer's subdomain slug from a hostname, e.g.
// "darrinspool.heberthoops.com" -> "darrinspool". Returns null for the
// bare root domain, "www", and anything that isn't a *.heberthoops.com
// hostname at all (including localhost/dev, so this never affects local
// development regardless of how the dev server's hostname is configured).
export function extractOrganizerSlug(hostname) {
    if (!hostname.endsWith(`.${ROOT_DOMAIN}`)) return null;
    const subdomain = hostname.slice(0, -(ROOT_DOMAIN.length + 1));
    if (!subdomain || subdomain === 'www') return null;
    return subdomain;
}

// True for the bare root domain or "www" -- i.e. NOT an organizer
// subdomain and NOT localhost/dev. Used to gate the auto-redirect so it
// only fires on a real production-style root/www visit.
export function isRootOrWwwHost(hostname) {
    return hostname === ROOT_DOMAIN || hostname === `www.${ROOT_DOMAIN}`;
}

// Builds the full URL for a given organizer slug's subdomain, preserving
// the current protocol and port (if any) and stripping any subdomain
// that's currently present in `host`.
export function buildSubdomainUrl(slug, { protocol, host }) {
    // Preserve the port if present (host may be "host:port"), and derive
    // the root domain's label count from ROOT_DOMAIN itself rather than
    // hardcoding "last 2 labels" -- that assumption broke for a 3-label
    // root domain like staging.heberthoops.com (it would keep only
    // "heberthoops.com", silently dropping "staging.").
    const [hostname, port] = host.split(':');
    const rootLabelCount = ROOT_DOMAIN.split('.').length;
    const rootDomain = hostname.split('.').slice(-rootLabelCount).join('.');
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}//${slug}.${rootDomain}${portSuffix}`;
}

export { ROOT_DOMAIN };